import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { 
  ArrowLeft, Coins, ChevronDown, ChevronUp, 
  Filter, Calendar, User, RefreshCw, X, TrendingUp, TrendingDown, Gift, Link2, Info
} from "lucide-react";
import { formatInTimezone } from "../utils/timezone";
import AdminMenu from "../components/AdminMenu";

const PromptLogBonusPool = () => {
  const navigate = useNavigate();
  const { userId: urlUserId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [totals, setTotals] = useState({
    total_credits: 0,
    total_debits: 0,
    net_change: 0,
    transaction_count: 0,
    pool_balance: 0
  });
  const [pagination, setPagination] = useState({
    skip: 0,
    limit: 50,
    has_more: false,
    total_count: 0
  });
  const [availableTypes, setAvailableTypes] = useState([]);
  const [showTotalsDropdown, setShowTotalsDropdown] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  
  // Filter states
  const [selectedType, setSelectedType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [userId, setUserId] = useState(urlUserId || "");
  
  // Applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    type: "",
    fromDate: "",
    toDate: "",
    userId: urlUserId || ""
  });

  // Get user timezone
  const userTimezone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const fetchBonusPool = useCallback(async (skip = 0, filters = appliedFilters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip);
      params.append("limit", 50);
      
      if (filters.type) params.append("transaction_type", filters.type);
      if (filters.fromDate) params.append("from_date", new Date(filters.fromDate).toISOString());
      if (filters.toDate) params.append("to_date", new Date(filters.toDate).toISOString());
      if (filters.userId) params.append("user_id", filters.userId);
      
      const response = await authAxios.get(`/credits/admin/bonus-pool?${params.toString()}`);
      
      setTransactions(response.data.transactions || []);
      setTotals(response.data.totals || {});
      setPagination(response.data.pagination || {});
      setAvailableTypes(response.data.available_types || []);
    } catch (error) {
      console.error("Failed to fetch bonus pool:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    if (urlUserId) {
      const initialFilters = { ...appliedFilters, userId: urlUserId };
      setAppliedFilters(initialFilters);
      fetchBonusPool(0, initialFilters);
    } else {
      fetchBonusPool(0);
    }
  }, [urlUserId]);

  const applyFilters = () => {
    const newFilters = {
      type: selectedType,
      fromDate,
      toDate,
      userId
    };
    setAppliedFilters(newFilters);
    fetchBonusPool(0, newFilters);
  };

  const clearFilters = () => {
    setSelectedType("");
    setFromDate("");
    setToDate("");
    setUserId("");
    const emptyFilters = { type: "", fromDate: "", toDate: "", userId: "" };
    setAppliedFilters(emptyFilters);
    fetchBonusPool(0, emptyFilters);
  };

  const hasActiveFilters = appliedFilters.type || appliedFilters.fromDate || appliedFilters.toDate || appliedFilters.userId;

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total_count / pagination.limit);

  // Format transaction type for display
  const formatType = (type) => {
    const typeColors = {
      PURCHASE_CREDIT: "bg-green-500/20 text-green-400",
      BONUS_DEBIT: "bg-amber-500/20 text-amber-400",
      TASK_REWARD_DEBIT: "bg-purple-500/20 text-purple-400",
      STREAK_REWARD_DEBIT: "bg-cyan-500/20 text-cyan-400",
      ACHIEVEMENT_REWARD_DEBIT: "bg-pink-500/20 text-pink-400",
      DISCOUNT_DEBIT: "bg-orange-500/20 text-orange-400",
      MANUAL_ADJUST: "bg-gray-500/20 text-gray-400"
    };
    return typeColors[type] || "bg-gray-500/20 text-gray-400";
  };

  // Format type label
  const formatTypeLabel = (type) => {
    const labels = {
      PURCHASE_CREDIT: "Purchase",
      BONUS_DEBIT: "Bonus",
      TASK_REWARD_DEBIT: "Task",
      STREAK_REWARD_DEBIT: "Streak",
      ACHIEVEMENT_REWARD_DEBIT: "Achievement",
      DISCOUNT_DEBIT: "Discount",
      MANUAL_ADJUST: "Manual"
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-divine-black/95 backdrop-blur border-b border-divine-gold/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back link and title */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/plog")}
                className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Prompt Log</span>
              </button>
              <h1 className="font-cinzel text-xl text-divine-gold flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Bonus Coin Pool
              </h1>
            </div>
            
            {/* Right: Totals display */}
            <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setShowTotalsDropdown(!showTotalsDropdown)}
                className="flex items-center gap-4 px-4 py-2 bg-white/5 rounded-lg border border-divine-gold/30 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Coins className="w-4 h-4 text-amber-400" />
                  <span className={`font-medium ${totals.pool_balance >= 0 ? 'text-white' : 'text-red-400'}`}>
                    {totals.pool_balance?.toLocaleString() || 0}
                  </span>
                  <span className="text-white/40 text-sm">balance</span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 font-medium">
                    +{totals.total_credits?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="w-px h-6 bg-white/20" />
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-red-400" />
                  <span className="text-red-400 font-medium">
                    -{totals.total_debits?.toLocaleString() || 0}
                  </span>
                </div>
                {showTotalsDropdown ? (
                  <ChevronUp className="w-4 h-4 text-white/40" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-white/40" />
                )}
              </button>
              
              {/* Totals Dropdown */}
              {showTotalsDropdown && (
                <div className="absolute right-0 top-full mt-2 w-72 bg-divine-charcoal border border-divine-gold/30 rounded-lg shadow-xl z-50 p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-white/60">Total Transactions</span>
                      <span className="text-white font-medium">{totals.transaction_count?.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-white/10 pt-3">
                      <div className="flex justify-between">
                        <span className="text-white/60">Total Credits (Purchases)</span>
                        <span className="text-green-400 font-medium">+{totals.total_credits?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between mt-2">
                        <span className="text-white/60">Total Debits (Bonuses)</span>
                        <span className="text-red-400 font-medium">-{totals.total_debits?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                        <span className="text-white/60">Pool Balance</span>
                        <span className={`font-medium ${totals.pool_balance >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                          {totals.pool_balance?.toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowInfoModal(true); setShowTotalsDropdown(false); }}
                      className="w-full mt-3 pt-3 border-t border-white/10 flex items-center justify-center gap-2 text-divine-gold/80 hover:text-divine-gold text-sm transition-colors"
                      data-testid="bonus-pool-info-btn"
                    >
                      <Info className="w-3.5 h-3.5" /> How does the Bonus Pool work?
                    </button>
                  </div>
                </div>
              )}
            </div>
            <AdminMenu />
            </div>
          </div>
        </div>
      </header>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setShowInfoModal(false)} data-testid="bonus-pool-info-modal">
          <div className="w-full max-w-lg bg-divine-charcoal border border-divine-gold/30 rounded-lg shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-divine-gold/20 flex items-center justify-between">
              <h2 className="font-cinzel text-lg text-divine-gold flex items-center gap-2"><Gift className="w-5 h-5" /> Bonus Pool</h2>
              <button onClick={() => setShowInfoModal(false)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-5 space-y-5 text-sm text-white/80 max-h-[70vh] overflow-y-auto">
              <div>
                <h3 className="text-white font-semibold mb-1">What is the Bonus Pool?</h3>
                <p>Each user has their own Bonus Pool — a separate, hidden credit account that tracks how bonuses are funded and distributed. It operates alongside the main Coin Ledger but is not visible to end users.</p>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-1">Header Totals</h3>
                <div className="space-y-2 pl-2">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-400 font-medium min-w-[70px]">Balance</span>
                    <span>The user's current bonus pool balance (credits minus debits). Can go <span className="text-red-400">negative</span> — this is by design and acts as a hidden credit line.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-green-400 font-medium min-w-[70px]">Credits</span>
                    <span>Total coins credited to the pool. Currently funded by <span className="text-green-400">coin purchases</span> — when a user buys coins, the base purchase amount is added here.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-red-400 font-medium min-w-[70px]">Debits</span>
                    <span>Total coins deducted from the pool when bonuses are awarded to the user's main Coin Ledger.</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-1">How Bonuses Flow</h3>
                <div className="bg-divine-black/50 rounded-lg p-3 border border-white/5 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400">Purchase</span>
                    <span className="text-white/50">User buys 500 coins for $4.99</span>
                  </div>
                  <div className="pl-4 border-l border-white/10 ml-2 space-y-1">
                    <p>Coin Ledger: <span className="text-green-400">+600</span> (500 base + 100 bonus)</p>
                    <p>Bonus Pool: <span className="text-green-400">+500</span> (PURCHASE_CREDIT)</p>
                    <p>Bonus Pool: <span className="text-red-400">-100</span> (BONUS_DEBIT)</p>
                    <p className="text-white/40">Net pool change: +400</p>
                  </div>
                </div>
                <div className="bg-divine-black/50 rounded-lg p-3 border border-white/5 space-y-2 text-xs mt-2">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">Task Reward</span>
                    <span className="text-white/50">User claims 500 coin email reward</span>
                  </div>
                  <div className="pl-4 border-l border-white/10 ml-2 space-y-1">
                    <p>Coin Ledger: <span className="text-green-400">+500</span> (TASK_REWARD)</p>
                    <p>Bonus Pool: <span className="text-red-400">-500</span> (TASK_REWARD_DEBIT)</p>
                    <p className="text-white/40">Pool balance decreases by 500</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-1">Transaction Types</h3>
                <div className="grid grid-cols-1 gap-1.5 pl-2">
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-xs">Purchase</span><span>Base coins from a coin purchase (credit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">Bonus</span><span>Bonus coins given on purchase (debit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-400 text-xs">Task</span><span>Email read rewards, claim buttons (debit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-400 text-xs">Streak</span><span>Login streak rewards (debit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-pink-500/20 text-pink-400 text-xs">Achievement</span><span>Achievement milestone rewards (debit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 text-xs">Discount</span><span>Promotional discounts (debit)</span></div>
                  <div className="flex items-center gap-2"><span className="px-2 py-0.5 rounded bg-gray-500/20 text-gray-400 text-xs">Manual</span><span>Admin adjustments (credit or debit)</span></div>
                </div>
              </div>

              <div className="text-white/40 text-xs border-t border-white/10 pt-3">
                A negative pool balance means the user has received more bonuses than their purchases have funded. This is normal — the pool acts as a hidden credit system with no hard floor.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters Section */}
        <div className="mb-6 p-4 bg-white/5 rounded-lg border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-divine-gold" />
            <span className="text-white/80 font-medium">Filters</span>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                <X className="w-3 h-3" />
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Transaction Type Filter */}
            <div>
              <label className="block text-white/60 text-xs mb-1">Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              >
                <option value="">All Types</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>{formatTypeLabel(type)}</option>
                ))}
              </select>
            </div>
            
            {/* User Filter */}
            <div>
              <label className="block text-white/60 text-xs mb-1">User ID</label>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Enter user ID..."
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none placeholder:text-white/30"
              />
            </div>
            
            {/* From Date */}
            <div>
              <label className="block text-white/60 text-xs mb-1">From Date</label>
              <input
                type="datetime-local"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* To Date */}
            <div>
              <label className="block text-white/60 text-xs mb-1">To Date</label>
              <input
                type="datetime-local"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 bg-divine-charcoal border border-white/20 rounded-lg text-white text-sm focus:border-divine-gold/50 focus:outline-none"
              />
            </div>
            
            {/* Apply Button */}
            <div className="flex items-end">
              <button
                onClick={applyFilters}
                className="w-full px-4 py-2 bg-divine-gold/20 text-divine-gold border border-divine-gold/30 rounded-lg hover:bg-divine-gold/30 transition-colors text-sm font-medium"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-[1fr_1.2fr_0.8fr_0.8fr_0.8fr_1.2fr_1fr] gap-4 px-4 py-3 bg-white/5 border-b border-white/10 text-xs text-white/60 font-medium">
            <div>Time</div>
            <div>User</div>
            <div>Type</div>
            <div className="text-right">Amount</div>
            <div className="text-right">Balance After</div>
            <div>Reference</div>
            <div>Coin Ledger ID</div>
          </div>
          
          {/* Table Body */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 text-divine-gold animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-white/40">
              <Gift className="w-12 h-12 mb-4 opacity-50" />
              <p>No bonus pool transactions found</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {transactions.map((tx) => (
                <div 
                  key={tx.id} 
                  className="grid grid-cols-[1fr_1.2fr_0.8fr_0.8fr_0.8fr_1.2fr_1fr] gap-4 px-4 py-3 hover:bg-white/5 transition-colors items-center"
                >
                  {/* Time */}
                  <div className="text-sm text-white/60">
                    {tx.created_at ? formatInTimezone(new Date(tx.created_at), userTimezone) : "—"}
                  </div>
                  
                  {/* User */}
                  <div className="flex flex-col">
                    <span className="text-sm text-white truncate" title={tx.user_name || tx.user_id}>
                      {tx.user_name || tx.user_id?.slice(0, 12)}
                    </span>
                    <span className="text-xs text-white/40 truncate" title={tx.user_id}>
                      {tx.user_id}
                    </span>
                  </div>
                  
                  {/* Type */}
                  <div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${formatType(tx.type)}`}>
                      {formatTypeLabel(tx.type)}
                    </span>
                  </div>
                  
                  {/* Amount */}
                  <div className={`text-right text-sm font-medium ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount >= 0 ? '+' : ''}{tx.amount?.toLocaleString()}
                  </div>
                  
                  {/* Balance After */}
                  <div className={`text-right text-sm font-medium ${tx.balance_after >= 0 ? 'text-amber-400' : 'text-red-400'}`}>
                    {tx.balance_after?.toLocaleString()}
                  </div>
                  
                  {/* Reference */}
                  <div className="text-xs text-white/60 truncate" title={tx.reference_id || "—"}>
                    {tx.reference_id || "—"}
                  </div>
                  
                  {/* Coin Ledger ID */}
                  <div className="text-xs text-white/40 truncate font-mono" title={tx.coin_ledger_id || "—"}>
                    {tx.coin_ledger_id ? (
                      <span className="flex items-center gap-1">
                        <Link2 className="w-3 h-3" />
                        {tx.coin_ledger_id.slice(0, 12)}...
                      </span>
                    ) : "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_count > 0 && (
          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-white/60">
              Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total_count)} of {pagination.total_count}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchBonusPool(pagination.skip - pagination.limit)}
                disabled={pagination.skip === 0}
                className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-white/60">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => fetchBonusPool(pagination.skip + pagination.limit)}
                disabled={!pagination.has_more}
                className="px-3 py-1 bg-white/10 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/20 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PromptLogBonusPool;
